import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import { NEWS_CATEGORIES, NEWS_PAGE_SIZE, newsList, sortNewsByTime } from "@/lib/news";

const TABS = ["全部", ...NEWS_CATEGORIES] as const;

export default function News() {
  const [tab, setTab] = useState<(typeof TABS)[number]>("全部");
  const [visible, setVisible] = useState(NEWS_PAGE_SIZE);

  const all = useMemo(() => sortNewsByTime(newsList), []);
  const filtered = useMemo(
    () => (tab === "全部" ? all : all.filter((n) => n.category === tab)),
    [all, tab]
  );
  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const changeTab = (next: (typeof TABS)[number]) => {
    setTab(next);
    setVisible(NEWS_PAGE_SIZE);
  };

  return (
    <>
      <PageMeta
        title="影视资讯-蜜桃成熟行业动态与剧集新闻"
        description="蜜桃成熟影视资讯频道，聚合电影新闻、电视剧新闻与行业动态，第一时间了解影视圈的最新消息。"
      />

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />

        <div className="mt-5 mb-6">
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
            影视资讯
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            收录 {all.length} 条资讯，涵盖电影、电视剧与行业动态。
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          {TABS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => changeTab(item)}
              className={`px-4 py-2 rounded-full text-sm border whitespace-nowrap transition-colors cursor-pointer ${
                tab === item
                  ? "bg-primary-500 border-primary-500 text-background-50"
                  : "bg-background-50 border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <EmptyState
            icon="ri-newspaper-line"
            title="暂无相关资讯"
            description="该主题下暂时没有内容，切换其他主题看看。"
            actionLabel="查看全部资讯"
            onAction={() => changeTab("全部")}
          />
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {shown.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/detail/${item.id}`}
                  className="group flex flex-col sm:flex-row gap-4 md:gap-6 p-4 md:p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                >
                  <div className="w-full sm:w-56 h-40 sm:h-36 shrink-0 rounded-md overflow-hidden bg-background-100">
                    <img
                      src={item.image}
                      alt={item.title}
                      title={`${item.title} 资讯配图`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-800">
                        {item.category}
                      </span>
                      <span className="text-foreground-400">{item.publishTime}</span>
                    </div>
                    <h2 className="text-base md:text-lg font-medium text-foreground-950 leading-snug mt-3 line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed mt-2 line-clamp-2">
                      {item.summary}
                    </p>
                    <p className="text-xs text-foreground-400 mt-3">{item.author}</p>
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-10">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + NEWS_PAGE_SIZE)}
                  className="px-6 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap transition-colors cursor-pointer"
                >
                  加载更多
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}